'use client';

import { useEffect, useState } from 'react';
import {
  type IconProps,
  AirplaneIcon,
  AirplaneTakeoffIcon,
  AlarmIcon,
  ArmchairIcon,
  ArrowCounterClockwiseIcon,
  ArrowsDownUpIcon,
  ArrowsLeftRightIcon,
  BabyIcon,
  BankIcon,
  BarbellIcon,
  BedIcon,
  BeerSteinIcon,
  BicycleIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingIcon,
  BuildingsIcon,
  BusIcon,
  CalculatorIcon,
  CarIcon,
  CertificateIcon,
  ChalkboardTeacherIcon,
  ChartLineUpIcon,
  ChartPieSliceIcon,
  CircleDashedIcon,
  CoffeeIcon,
  CoinsIcon,
  ConfettiIcon,
  CpuIcon,
  CreditCardIcon,
  CurrencyCircleDollarIcon,
  DeviceMobileIcon,
  DiceFiveIcon,
  DropIcon,
  EyeIcon,
  FileArrowUpIcon,
  FileTextIcon,
  FileXlsIcon,
  FilmSlateIcon,
  FireIcon,
  FlagIcon,
  ForkKnifeIcon,
  GameControllerIcon,
  GarageIcon,
  GasPumpIcon,
  GearIcon,
  GlobeIcon,
  GraduationCapIcon,
  HamburgerIcon,
  HandCoinsIcon,
  HandshakeIcon,
  HeartbeatIcon,
  HeartIcon,
  HouseIcon,
  KeyIcon,
  LampIcon,
  LightningIcon,
  LockIcon,
  MapPinIcon,
  MedalIcon,
  MoneyIcon,
  MonitorIcon,
  MonitorPlayIcon,
  MusicNoteIcon,
  NotePencilIcon,
  PaperclipIcon,
  PaperPlaneRightIcon,
  PawPrintIcon,
  PercentIcon,
  PiggyBankIcon,
  PillIcon,
  PizzaIcon,
  PlantIcon,
  PlugIcon,
  PulseIcon,
  QrCodeIcon,
  ReceiptIcon,
  RoadHorizonIcon,
  RobotIcon,
  ScalesIcon,
  ShieldIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  SmileyIcon,
  SparkleIcon,
  StethoscopeIcon,
  TaxiIcon,
  TelevisionIcon,
  TicketIcon,
  TrainIcon,
  TrendUpIcon,
  TrophyIcon,
  TShirtIcon,
  UsersIcon,
  WalletIcon,
  WarningIcon,
  WifiHighIcon,
  WineIcon,
  WrenchIcon,
} from '@phosphor-icons/react';

type IconComponent = React.ComponentType<IconProps>;

// Maps the kebab-case icon names stored on the backend (`pluggy_categories.icon`)
// to Phosphor components. The backend owns that column; it lives in its own repo.
// The kebab name is the Phosphor component name without the `Icon` suffix
// (e.g. `BankIcon` → `bank`, `TrendUpIcon` → `trend-up`).
const CATEGORY_ICONS: Record<string, IconComponent> = {
  airplane: AirplaneIcon,
  'airplane-takeoff': AirplaneTakeoffIcon,
  alarm: AlarmIcon,
  armchair: ArmchairIcon,
  'arrow-counter-clockwise': ArrowCounterClockwiseIcon,
  'arrows-down-up': ArrowsDownUpIcon,
  'arrows-left-right': ArrowsLeftRightIcon,
  baby: BabyIcon,
  bank: BankIcon,
  barbell: BarbellIcon,
  bed: BedIcon,
  'beer-stein': BeerSteinIcon,
  bicycle: BicycleIcon,
  'book-open': BookOpenIcon,
  briefcase: BriefcaseIcon,
  building: BuildingIcon,
  buildings: BuildingsIcon,
  bus: BusIcon,
  calculator: CalculatorIcon,
  car: CarIcon,
  certificate: CertificateIcon,
  'chalkboard-teacher': ChalkboardTeacherIcon,
  'chart-line-up': ChartLineUpIcon,
  'chart-pie-slice': ChartPieSliceIcon,
  'circle-dashed': CircleDashedIcon,
  coffee: CoffeeIcon,
  coins: CoinsIcon,
  confetti: ConfettiIcon,
  cpu: CpuIcon,
  'credit-card': CreditCardIcon,
  'currency-circle-dollar': CurrencyCircleDollarIcon,
  'device-mobile': DeviceMobileIcon,
  'dice-five': DiceFiveIcon,
  drop: DropIcon,
  eye: EyeIcon,
  'file-arrow-up': FileArrowUpIcon,
  'file-text': FileTextIcon,
  'file-xls': FileXlsIcon,
  'film-slate': FilmSlateIcon,
  fire: FireIcon,
  flag: FlagIcon,
  'fork-knife': ForkKnifeIcon,
  'game-controller': GameControllerIcon,
  garage: GarageIcon,
  'gas-pump': GasPumpIcon,
  gear: GearIcon,
  globe: GlobeIcon,
  'graduation-cap': GraduationCapIcon,
  hamburger: HamburgerIcon,
  'hand-coins': HandCoinsIcon,
  handshake: HandshakeIcon,
  heart: HeartIcon,
  heartbeat: HeartbeatIcon,
  house: HouseIcon,
  key: KeyIcon,
  lamp: LampIcon,
  lightning: LightningIcon,
  lock: LockIcon,
  'map-pin': MapPinIcon,
  medal: MedalIcon,
  money: MoneyIcon,
  monitor: MonitorIcon,
  'monitor-play': MonitorPlayIcon,
  'music-note': MusicNoteIcon,
  'note-pencil': NotePencilIcon,
  paperclip: PaperclipIcon,
  'paper-plane-right': PaperPlaneRightIcon,
  'paw-print': PawPrintIcon,
  percent: PercentIcon,
  'piggy-bank': PiggyBankIcon,
  pill: PillIcon,
  pizza: PizzaIcon,
  plant: PlantIcon,
  plug: PlugIcon,
  pulse: PulseIcon,
  'qr-code': QrCodeIcon,
  receipt: ReceiptIcon,
  'road-horizon': RoadHorizonIcon,
  robot: RobotIcon,
  scales: ScalesIcon,
  shield: ShieldIcon,
  'shopping-bag': ShoppingBagIcon,
  'shopping-cart': ShoppingCartIcon,
  smiley: SmileyIcon,
  sparkle: SparkleIcon,
  stethoscope: StethoscopeIcon,
  taxi: TaxiIcon,
  television: TelevisionIcon,
  ticket: TicketIcon,
  train: TrainIcon,
  'trend-up': TrendUpIcon,
  trophy: TrophyIcon,
  't-shirt': TShirtIcon,
  users: UsersIcon,
  wallet: WalletIcon,
  warning: WarningIcon,
  'wifi-high': WifiHighIcon,
  wine: WineIcon,
  wrench: WrenchIcon,
};

/**
 * The curated in-app icon vocabulary (statically bundled, renders synchronously).
 * Also the AI suggestion vocabulary — pt-BR labels live in `lib/category-icons.json`,
 * which a test keeps in lockstep with these keys.
 */
export const CATEGORY_ICON_NAMES: readonly string[] = Object.keys(CATEGORY_ICONS);

type CategoryIconProps = Omit<IconProps, 'name' | 'weight'> & {
  name?: string | null;
  weight?: IconProps['weight'];
  fallback?: React.ReactNode;
};

// Cheap pre-gate so garbage stored values never pull the dynamic-library chunk.
const PLAUSIBLE_NAME = /^[a-z0-9-]{1,50}$/;

// One shared handle to the lazily-loaded full-library loader; the chunk (glob
// key map) is only fetched the first time an off-map icon actually renders.
let dynamicApi: typeof import('@/lib/phosphor-dynamic') | null = null;
let dynamicApiPromise: Promise<typeof import('@/lib/phosphor-dynamic')> | null = null;

function DynamicCategoryIcon({ name, weight, fallback, className, ...props }: CategoryIconProps & { name: string }) {
  const [resolved, setResolved] = useState<IconComponent | null | undefined>(
    () => dynamicApi?.getLoadedIcon(name),
  );

  useEffect(() => {
    if (resolved !== undefined) return;
    let alive = true;
    (dynamicApiPromise ??= import('@/lib/phosphor-dynamic'))
      .then((api) => {
        dynamicApi = api;
        return api.loadIcon(name);
      })
      .then((icon) => {
        if (alive) setResolved(icon);
      })
      .catch(() => {
        if (alive) setResolved(null);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by name at the call site
  }, [name]);

  if (resolved === undefined) {
    // Reserve the box while the icon chunk loads: no flash, no layout shift.
    return <span aria-hidden className={className} />;
  }
  if (resolved === null) return <>{fallback}</>;
  const Icon = resolved;
  return <Icon weight={weight} className={className} {...props} />;
}

export function CategoryIcon({ name, weight = 'duotone', fallback = null, ...props }: CategoryIconProps) {
  if (!name) return <>{fallback}</>;
  const Icon = CATEGORY_ICONS[name];
  if (Icon) return <Icon weight={weight} {...props} />;
  if (!PLAUSIBLE_NAME.test(name)) return <>{fallback}</>;
  // Off-map but plausible: any icon from the full Phosphor library, loaded lazily.
  return <DynamicCategoryIcon key={name} name={name} weight={weight} fallback={fallback} {...props} />;
}
